import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
} from "../../lib/react-leaflet-lite";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Loader2, Search } from "lucide-react";
import { useBasemapTile } from "../../config/basemaps";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { createTeardropMarker } from "../../utils/mapHelpers";
import { searchAddresses, resolveAddress } from "../../utils/geocode";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const pinIcon = () =>
  createTeardropMarker({
    size: 24,
    color: "#F97316",
    borderColor: "#fff",
    borderWidth: 2,
  });

function MapClickHandler({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

/**
 * Cómo se le presenta al usuario la banda de calidad que devuelve addresses-api.
 *
 * Mostrarla es la mitad del valor de esta pantalla: sin ella el usuario ve una
 * lista donde todas las sugerencias parecen igual de confiables y tiene que
 * leer cada texto completo para decidir. Con ella, el caso frecuente —hay un
 * match exacto y está primero— se resuelve de un vistazo.
 */
const QUALITY_TONE = {
  exact: "success",
  approximate: "warning",
  unrelated: "error",
};

/**
 * Selector de dirección con autocompletado y pin arrastrable.
 *
 * La cascada (caché local → Pelias → Google) vive entera del lado de
 * addresses-api; acá sólo se consume `/v1/autocomplete` y, cuando la sugerencia
 * elegida llega sin coordenadas, `/v1/resolve`.
 *
 * Está modelado sobre el picker de `settings/tabs/LocationsTab.jsx`, que es el
 * que ya funciona en producción, pero es un componente aparte y no una
 * extracción: aquel está acoplado al formulario de bodegas y refactorizarlo sin
 * poder correr la suite del front sería cambiar algo que hoy anda por algo que
 * no puedo verificar.
 *
 * `instanceId` NO es cosmético: los ids del combobox de aquel componente son
 * literales, y con varias instancias en una misma pantalla —que es exactamente
 * el caso de la bandeja— se rompe la relación aria entre el input y su lista.
 */
export default function AddressPicker({
  instanceId,
  initialQuery = "",
  initialPoint = null,
  countryCode = "cl",
  focusCenter,
  confirmLabel,
  busy = false,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation();
  const tile = useBasemapTile();

  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSug, setLoadingSug] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [point, setPoint] = useState(initialPoint);
  const [chosenLabel, setChosenLabel] = useState(null);
  const [resolving, setResolving] = useState(false);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const mapRef = useRef(null);

  const listId = `ap-${instanceId}-list`;
  const optId = (i) => `ap-${instanceId}-opt-${i}`;

  const center = useMemo(
    () => initialPoint || focusCenter || { lat: -33.45, lng: -70.65 },
    [initialPoint, focusCenter],
  );

  const geoOpts = useMemo(
    () => ({ countryCode, center: focusCenter || center }),
    [countryCode, focusCenter, center],
  );

  const clearPending = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  useEffect(() => clearPending, [clearPending]);

  const runSearch = useCallback(
    (term) => {
      clearPending();
      const text = (term || "").trim();
      if (text.length < 3) {
        setSuggestions([]);
        setActiveIdx(-1);
        setLoadingSug(false);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        const controller = new AbortController();
        abortRef.current = controller;
        setLoadingSug(true);
        try {
          const data = await searchAddresses(text, {
            ...geoOpts,
            signal: controller.signal,
          });
          setSuggestions(data);
          setActiveIdx(-1);
        } catch {
          // Un fallo de red no puede dejar sugerencias viejas en pantalla: el
          // usuario elegiría una que ya no corresponde a lo que escribió.
          setSuggestions([]);
          setActiveIdx(-1);
        } finally {
          setLoadingSug(false);
        }
      }, 300);
    },
    [clearPending, geoOpts],
  );

  const onQueryChange = (value) => {
    setQuery(value);
    runSearch(value);
  };

  const pickSuggestion = async (s) => {
    if (!s) return;
    setSuggestions([]);
    setActiveIdx(-1);
    setQuery(s.label);
    setChosenLabel(s.label);

    let lat = s.lat;
    let lng = s.lng;

    // Las predicciones de Google llegan sin coordenadas: hay que resolverlas.
    if (s.needsResolution || lat == null || lng == null) {
      setResolving(true);
      try {
        const r = await resolveAddress(s.id, { source: s.source });
        lat = r?.lat ?? null;
        lng = r?.lng ?? null;
      } catch {
        lat = null;
        lng = null;
      } finally {
        setResolving(false);
      }
    }

    if (lat == null || lng == null) return;
    setPoint({ lat, lng });
    mapRef.current?.flyTo([lat, lng], 17, { animate: true });
  };

  const onKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setSuggestions([]);
      setActiveIdx(-1);
    }
  };

  const canConfirm = !!point && !busy && !resolving;

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <label className="sr-only" htmlFor={`ap-${instanceId}-input`}>
          {t("Buscar dirección")}
        </label>
        <div className="relative">
          <Search
            size={16}
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"
          />
          <input
            id={`ap-${instanceId}-input`}
            type="text"
            role="combobox"
            aria-expanded={suggestions.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeIdx >= 0 ? optId(activeIdx) : undefined}
            autoComplete="off"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("Escribe calle y número")}
            className="w-full rounded-lg border border-border bg-bg py-2 pl-9 pr-9 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
          />
          {(loadingSug || resolving) && (
            <Loader2
              size={16}
              aria-hidden="true"
              className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-fg-muted"
            />
          )}
        </div>

        {suggestions.length > 0 && (
          <ul
            id={listId}
            role="listbox"
            className="absolute z-[1000] mt-1 max-h-64 w-full overflow-auto rounded-lg border border-border bg-bg shadow-lg"
          >
            {suggestions.map((s, i) => (
              <li
                key={s.id}
                id={optId(i)}
                role="option"
                aria-selected={i === activeIdx}
                onMouseDown={(e) => {
                  // mousedown y no click: el blur del input cerraría la lista antes.
                  e.preventDefault();
                  pickSuggestion(s);
                }}
                onMouseEnter={() => setActiveIdx(i)}
                className={`flex cursor-pointer items-start justify-between gap-2 px-3 py-2 text-sm ${
                  i === activeIdx ? "bg-brand-50" : ""
                }`}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{s.label}</span>
                  {s.subtitle && (
                    <span className="block truncate text-xs text-fg-muted">
                      {s.subtitle}
                    </span>
                  )}
                </span>
                {s.matchQuality && (
                  <Badge
                    tone={QUALITY_TONE[s.matchQuality] || "neutral"}
                    className="shrink-0"
                  >
                    {s.matchQuality === "exact"
                      ? t("Exacta")
                      : s.matchQuality === "approximate"
                        ? t("Aproximada")
                        : t("Otra calle")}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="h-64 overflow-hidden rounded-lg border border-border">
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={point ? 17 : 12}
          scrollWheelZoom
          style={{ height: "100%", width: "100%" }}
          ref={mapRef}
          aria-label={t("Selector de ubicación en el mapa")}
        >
          <TileLayer
            key={tile.url}
            url={tile.url}
            attribution={tile.attribution}
            subdomains={tile.subdomains}
            maxZoom={tile.maxZoom}
          />
          <MapClickHandler onMapClick={(ll) => setPoint(ll)} />
          {point && (
            <Marker
              position={[point.lat, point.lng]}
              icon={pinIcon()}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const ll = e.target.getLatLng();
                  setPoint({ lat: ll.lat, lng: ll.lng });
                },
              }}
            />
          )}
        </MapContainer>
      </div>

      <p className="flex items-center gap-1.5 text-xs text-fg-muted">
        <MapPin size={13} aria-hidden="true" />
        {point
          ? t("Arrastra el pin o haz clic en el mapa para ajustar la ubicación exacta.")
          : t("Elige una sugerencia o haz clic en el mapa para fijar la ubicación.")}
      </p>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {t("Cancelar")}
          </Button>
        )}
        <Button
          onClick={() =>
            onConfirm?.({ lat: point.lat, lng: point.lng, label: chosenLabel })
          }
          disabled={!canConfirm}
        >
          {busy && <Loader2 size={15} className="animate-spin" aria-hidden="true" />}
          {confirmLabel || t("Guardar ubicación")}
        </Button>
      </div>
    </div>
  );
}
